import { describeAxiosError, extractApiMessage, statusFallbackMessage } from '../common/api-message';
import {
  ADD_COUPON,
  ADMIN_CLIENT_COMPANY,
  ADMIN_CLIENT_OWNER,
  ADMIN_CLIENT_OWNER_USERS,
  ADMIN_NOTIFICATION_DATA,
  ASSIGN_ASSISTANT,
  ASSISTANT,
  ATTACH_PAYMENT_METHOD,
  AUTH_TOKEN,
  AVAILABLE_ASSISTANTS,
  CALENDAR_ENTRIES,
  CALENDAR_ENTRY,
  CALENDAR_ENTRY_RECURRENCES,
  CALENDAR_ENTRY_REMINDER_TIME,
  CALENDAR_ENTRIES_UNREAD,
  CALENDAR_ENTRY_SEEN,
  CALENDAR_ENTRY_TYPES,
  CALENDAR_MONTHLY_AVAILABILITY,
  CALENDAR_NOTIFICATION_DATA,
  CALENDAR_USERS,
  CHANGE_DEFAULT_PAYMENT_METHOD,
  CHANGE_PASSWORD,
  CHATROOM_USERS,
  CHAT_GROUPS_OVERSEEN,
  CHAT_MESSAGES,
  CHAT_ROOM_MESSAGES,
  CHAT_ROOM_MESSAGES_OVERSEEN,
  CHAT_ROOM_MESSAGES_OVERSEEN_ADDITIONAL,
  CLIENT,
  CLIENTS_FOR_ADMIN_CLIENT,
  COMPANY,
  REGISTRATIONS_OVERVIEW,
  CREATE_USER,
  CREATE_USER_LIMIT,
  CREATE_USER_WITH_REGISTRATION_LINK,
  DELETE_GUEST_TASK,
  DELETE_PAYMENT_METHOD,
  DOCUMENT,
  DOCUMENTS,
  EDITOR_SESSION,
  GET_EMAIL_INVITATIONS,
  GET_PAYMENT_METHODS,
  GET_STRIPE_CUSTOMER,
  GUEST_AVAILABLE_TASKS,
  GUEST_EXISTING,
  GUEST_TASK,
  GUEST_USER,
  LATEST_COMMENTS,
  LATEST_DOCUMENTS,
  LOGGED_USER,
  NOTIFICATION_DATA,
  NOTIFICATION_FEED,
  NOTIFICATION_MARK_ALL_READ,
  NOTIFICATION_MARK_CHAT_ROOM_READ,
  NOTIFICATION_FREQUENCIES,
  PHONEBOOK_AC_COMPANY_CONTACTS,
  PHONEBOOK_CONTACT,
  PHONEBOOK_CONTACTS,
  PHONEBOOK_CONTACTS_DELETE,
  PHONEBOOK_CONTACT_DETAIL_TYPES,
  PHONEBOOK_USERS,
  PUSH_ON_CLIENT_DATA,
  PUSH_ON_SETTINGS,
  RECOVER_PASSWORD,
  REGISTER_USER_WITH_LINK,
  REGISTRATION,
  REMOVE_COUPON,
  RESEND_REGISTRATION_LINK,
  RESET_PASSWORD,
  MEDIA_STORAGE,
  SETTINGS,
  SET_TASK_COMMENTS_SEEN,
  SPECIFIC_USER,
  TASK,
  TASKS,
  TASK_ASSIGNEE,
  TASK_AVAILABLE_USERS_FOR_AC_COMPANY,
  TASK_COMMENTS,
  TASK_COMMENT_NOTIFICATION_DATA,
  TASK_DOCUMENT_NOTIFICATION_DATA,
  TASK_NOTIFICATION_DATA,
  TASK_STATUS,
  TASK_STATUSES,
  UNASSIGN_ASSISTANT,
  UPDATE_REGISTRATION,
  USER,
  USER_ACTIVITY,
  USER_BASE,
  USER_DATA,
  USER_EMAIL,
  USER_SETTINGS,
  VALIDATE_DATA,
  SUBSCRIPTION_STATUS,
  DEFAULT_PAYMENT_METHOD_FROM_INTENT,
  STRIPE_SYNC_HELD_DEACTIVATIONS,
  STRIPE_SYNC_RECONCILIATION,
  STRIPE_SYNC_FAILED_EVENTS,
} from './api-routes';

import { API_BASE_URL } from '../pages/Products/api';
import { ApiError } from '../common/ApiError';
import { compressThumbnail, prepareAvatar } from '../common/media-compression';
import { INTIAL_OVERSEEN_MESSAGE_COUNT_FETCH } from '../common/appConstants';
import axios from 'axios';
import { getActingOwnerId } from './acting-owner';
import { setAuthorizationToken } from '../utilities/api-helper';
import moment from 'moment';

const inFlightRequests = new Map();
const lastUserActivityRequest = new Map();
const USER_ACTIVITY_THROTTLE_MS = 30000;

function getResponseResult(response) {
  if (!response.data.Success) {
    return Promise.reject(new ApiError(response.data));
  }
  return response.data.Result;
}

function getUploadValue(upload, fieldName) {
  return upload?.[fieldName] ?? upload?.[fieldName.charAt(0).toLowerCase() + fieldName.slice(1)];
}

function getFileExtension(fileName) {
  const parts = (fileName || '').split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function dataUrlToFile(dataUrl, fileName) {
  const [header, base64] = dataUrl.split(',');
  const match = header.match(/data:(.*?);base64/);
  const contentType = match?.[1] || 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], fileName, { type: contentType });
}

function extractFile(selectedFile) {
  if (selectedFile instanceof FormData) {
    return selectedFile.get('file');
  }
  return selectedFile;
}

function canCreateChatThumbnail(file, mediaType) {
  if (!file) return false;
  const contentType = (file.type || '').toLowerCase();
  const extension = getFileExtension(file.name);

  if (mediaType === 'image') {
    return contentType.startsWith('image/') && extension !== 'heic' && extension !== 'heif' && extension !== 'svg';
  }

  if (mediaType === 'video') {
    return contentType.startsWith('video/');
  }

  return false;
}

function canvasToJpegFile(canvas, fileName) {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }

        resolve(new File([blob], fileName, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.82
    );
  });
}

function fitInside(width, height, maxSize) {
  if (!width || !height) {
    return { width: maxSize, height: maxSize };
  }

  const ratio = Math.min(maxSize / width, maxSize / height, 1);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

async function createImageThumbnail(file, maxSize = 640) {
  const imageUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageUrl;
    });

    const size = fitInside(image.naturalWidth || image.width, image.naturalHeight || image.height, maxSize);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;

    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, size.width, size.height);

    return canvasToJpegFile(canvas, `${file.name || 'image'}-thumb.jpg`);
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

async function createVideoThumbnail(file, maxSize = 640) {
  const videoUrl = URL.createObjectURL(file);

  try {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = videoUrl;

    await new Promise((resolve, reject) => {
      video.onloadedmetadata = resolve;
      video.onerror = reject;
    });

    if (Number.isFinite(video.duration) && video.duration > 0.1) {
      const targetTime = Math.min(1, Math.max(0.1, video.duration / 2));
      await new Promise((resolve, reject) => {
        video.onseeked = resolve;
        video.onerror = reject;
        video.currentTime = targetTime;
      });
    } else {
      await new Promise((resolve, reject) => {
        video.onloadeddata = resolve;
        video.onerror = reject;
        video.load();
      });
    }

    const size = fitInside(video.videoWidth, video.videoHeight, maxSize);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;

    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, size.width, size.height);

    return canvasToJpegFile(canvas, `${file.name || 'video'}-thumb.jpg`);
  } finally {
    URL.revokeObjectURL(videoUrl);
  }
}

async function createChatThumbnail(file, mediaType) {
  if (!canCreateChatThumbnail(file, mediaType)) {
    return null;
  }

  try {
    let thumbnail = null;

    if (mediaType === 'image') {
      thumbnail = await createImageThumbnail(file);
    } else if (mediaType === 'video') {
      thumbnail = await createVideoThumbnail(file);
    }

    // Thumbnails for both images and videos target 80 KB.
    return thumbnail ? await compressThumbnail(thumbnail) : null;
  } catch {
    return null;
  }
}

const UPLOAD_TIMEOUT_BASE_MS = 60 * 1000;
const UPLOAD_MIN_BYTES_PER_SECOND = 64 * 1024;

function uploadTimeoutSignal(sizeBytes) {
  if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') return undefined;
  return AbortSignal.timeout(UPLOAD_TIMEOUT_BASE_MS + ((sizeBytes || 0) / UPLOAD_MIN_BYTES_PER_SECOND) * 1000);
}

function putWithProgress(uploadUrl, contentType, file, onProgress) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', uploadUrl);
    request.setRequestHeader('Content-Type', contentType);
    request.timeout = UPLOAD_TIMEOUT_BASE_MS + ((file.size || 0) / UPLOAD_MIN_BYTES_PER_SECOND) * 1000;
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new Error(`S3 upload failed with status ${request.status}`));
    };
    request.onerror = () => reject(new Error('S3 upload failed'));
    request.ontimeout = () => reject(new Error('S3 upload timed out'));
    request.send(file);
  });
}

async function uploadToPresignedUrl(upload, file, signal, onProgress) {
  const uploadUrl = getUploadValue(upload, 'UploadUrl');
  if (!uploadUrl) {
    throw new Error('Missing presigned upload URL');
  }

  const contentType = getUploadValue(upload, 'ContentType') || file.type || 'application/octet-stream';

  if (onProgress && typeof XMLHttpRequest !== 'undefined') {
    await putWithProgress(uploadUrl, contentType, file, onProgress);
    return;
  }

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: file,
    signal,
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed with status ${response.status}`);
  }
}

export function grantMediaAccessResult(key, metadata = {}) {
  return axios
    .post(`${MEDIA_STORAGE}/access`, {
      key,
      ...metadata,
    }, {
      withCredentials: true,
    })
    .then((response) => {
      if (!response.data.Success) {
        return null;
      }

      return response.data.Result ?? null;
    })
    .catch(() => null);
}

export function grantMediaAccess(key, metadata = {}) {
  return grantMediaAccessResult(key, metadata)
    .then((result) => result?.url ?? result?.Url ?? key)
    .catch(() => null);
}

export async function downloadChatMedia(key, fileName) {
  const result = await grantMediaAccessResult(key, { downloadFileName: fileName });
  const url = result?.url ?? result?.Url;
  if (!url) {
    return false;
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || '';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return true;
}

function dedupeRequest(key, requestFactory) {
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  const request = requestFactory().finally(() => {
    inFlightRequests.delete(key);
  });

  inFlightRequests.set(key, request);
  return request;
}

export class LoginError extends Error {
  constructor(message, data) {
    super(message);
    this._data = data;
  }

  get data() {
    return this._data;
  }
}

const SUBSCRIPTION_API_PATH = /\/api\/(subscriptions?|invoices|customers|products|counters\/usercount)(\/|\?|$)/i;
const NOTIFICATION_READ_PATH = /\/api\/Notification\/(Feed|Data(?!\/Admin))(\/|\?|$)/i;
const CALENDAR_READ_PATH = /\/api\/(CalendarEntries|CalendarEntry\/Unread)(\/|\?|$)/i;

const followsActingOwner = (config) => {
  const url = config.url || '';
  if (SUBSCRIPTION_API_PATH.test(url) || CALENDAR_READ_PATH.test(url)) return true;
  return (config.method || 'get').toLowerCase() === 'get' && NOTIFICATION_READ_PATH.test(url);
};

axios.interceptors.request.use((config) => {
  const actingOwnerId = getActingOwnerId();

  if (actingOwnerId && followsActingOwner(config)) {
    config.headers = { ...config.headers, 'X-Act-As-Owner': actingOwnerId };
  }
  return config;
});

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error?.response?.status === 401) {
      return {
        data: {
          Success: false,
          StatusCode: error.response.status,
          Message: extractApiMessage(error.response.data) ?? statusFallbackMessage(401),
        },
      };
    } else {
      return Promise.reject(error);
    }
  }
);

export function generateToken(data) {
  const customer = JSON.parse(localStorage.getItem('customer'));
  const loginForm = new URLSearchParams(data);
  loginForm.set('platform', 'web');

  return axios
    .post(AUTH_TOKEN, loginForm.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    .then((response) => {
      const token = response.data.access_token;
      localStorage.setItem('authToken', token);
      setAuthorizationToken(token);
      return { token, customer };
    })
    .catch((error) => {
      if (error.response) {
        const { data } = error.response;
        throw new LoginError(extractApiMessage(data) ?? statusFallbackMessage(error.response.status), data);
      } else throw error;
    });
}

export async function loginCheck() {
  try {
    const response = await axios.get(`${API_BASE_URL}/LoginCheck`);
    const nextPage = response.data.NextLoginPage;
    return {
      status: response.status,
      msg: nextPage,
      pendingInvoiceId: response?.data?.InvoiceId,
      isReturning: nextPage === 'ResumeSubscription',
    };
  } catch (e) {
    return { status: e.response?.status ?? 0, msg: describeAxiosError(e) };
  }
}

export async function getLastSubscriptionItems() {
  try {
    const authToken = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/subscriptions/lastsubscription/items`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function getLoggedUser() {
  const response = await axios.get(LOGGED_USER + '/true');
  if (!response.data.Success) {
    if (response.data.StatusCode === 401) {
      const loginCheckRes = await loginCheck();
      if (loginCheckRes.status === 400) {
        throw new ApiError({ Message: loginCheckRes.msg, StatusCode: 400 });
      }
      return { subscribed: false, pendingInvoiceId: loginCheckRes?.pendingInvoiceId, isReturning: loginCheckRes?.isReturning ?? false };
    }
    throw new ApiError(response.data);
  }
  if (response.data.Result != null) {
    return { ...response.data.Result, subscribed: true };
  }
}

export async function getLoggedUserFromToken(authToken) {
  const response = await axios.get(LOGGED_USER + '/true', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  if (response.data.Result != null) {
    return { ...response.data.Result, subscribed: true };
  }
}

export async function createEditorSession() {
  const authToken = localStorage.getItem('authToken');
  const response = await axios.post(
    EDITOR_SESSION,
    {},
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  );

  if (!response.data.Success) {
    throw new Error(response.data.Message || 'Unable to create editor session');
  }

  return response.data.Result;
}

export async function validateEditorSession(token) {
  const response = await axios.post(`${EDITOR_SESSION}/Validate`, { Token: token });

  if (!response.data.Success) {
    throw new Error(response.data.Message || 'Invalid editor session');
  }

  return response.data.Result;
}

export async function authenticateUser(data) {
  const { token } = await generateToken(data);

  if (token != null) {
    // logUserActivity(UserActivityStatus.Login);
    const user = await getLoggedUser();
    return Promise.resolve(user);
  }
  return Promise.reject(
    new ApiError({ Success: false, Message: 'Authentication failed.', StatusCode: 401 }),
  );
}

export async function registerUser(data) {
  return await axios.post(REGISTRATION, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject();
    }
    return { username: data.User_Username, password: data.User_PWD };
  });
}

export async function getCustomer() {
  return axios.post(`${API_BASE_URL}/customers`).then((response) => {
    if (response.status !== 200) {
      return Promise.reject();
    }
    return response.data;
  });
}

export async function checkBillingInfo(str) {
  if (str === 'noState') {
    return { status: true };
  }

  return axios
    .get(`${API_BASE_URL}/customers/checkCustomerAddress`)
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject();
      }

      const { data } = response;

      if (data.AutomaticTaxStatus === 'supported') {
        return {
          status: true,
          message:
            "The customer is located in a country or state where you're collecting tax",
        };
      }

      if (data.AutomaticTaxStatus === 'not_collecting') {
        return {
          status: false,
          message:
            "The customer is located in a country or state where you're not registered to collect tax.\nAlso returned when automatic tax calculation is not supported in the customer's location.",
        };
      }
      if (data.AutomaticTaxStatus === 'unrecognized_location') {
        return {
          status: false,
          message:
            "The customer's location couldn't be determined.\nMake sure the provided address information is valid and supported in the customer's country.",
        };
      }

      if (data.AutomaticTaxStatus === 'failed') {
        return {
          status: false,
          message:
            "There was an error determining the customer's location.\nThis is usually caused by a temporary issue. Retrieve the customer to try again.",
        };
      }

      return { status: false, message: 'Bad request' };
    });
}

export async function updateRegisterUser(data) {
  const authToken = localStorage.getItem('authToken');
  const config = {
    method: 'PUT',
    url: UPDATE_REGISTRATION,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    data: data,
  };

  const response = await axios(config);

  if (!response.data.Success) {
    return Promise.reject();
  }

  return { username: data.User_Username, password: data.User_PWD };
}

export function recoverPassword(data) {
  return axios.post(RECOVER_PASSWORD, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Message;
  });
}

export function changePassword(data) {
  return axios.post(CHANGE_PASSWORD, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function validateData(data) {
  return axios.post(VALIDATE_DATA, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function resetPassword(data) {
  return axios.post(RESET_PASSWORD, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Message;
  });
}

export const NOTIFICATIONS_CLEARED_AT_PREFIX = 'notificationsClearedAt:';

function getLoggedUserId() {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return null;

    const [, payload] = token.split('.');
    if (!payload) return null;

    const claims = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );

    return (
      claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
      claims.nameid ||
      null
    );
  } catch {
    return null;
  }
}

function getClearedAtKey() {
  const userId = getLoggedUserId();
  return userId ? NOTIFICATIONS_CLEARED_AT_PREFIX + userId : null;
}

export function getNotificationsClearedAt() {
  const key = getClearedAtKey();
  return key ? localStorage.getItem(key) || null : null;
}

export function clearAllNotifications() {
  const key = getClearedAtKey();
  const now = new Date().toISOString();

  if (key) {
    localStorage.setItem(key, now);
  }

  return Promise.resolve(now);
}

export function getNotificationFeed({ type, take = 20, skip = 0 } = {}) {
  return axios
    .get(NOTIFICATION_FEED, {
      params: {
        type: type || undefined,
        take,
        skip,
        utcOffsetMinutes: getUtcOffsetMinutes(),
        clearedAt: (!getActingOwnerId() && getNotificationsClearedAt()) || undefined,
      },
    })
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

// Unread calendar entries, oldest first - the calendar's "Unread" filter.
export function getUnreadCalendarEntries({ take = 20, skip = 0, userId, adminClientCompanyId } = {}) {
  return axios
    .get(CALENDAR_ENTRIES_UNREAD, {
      params: {
        take,
        skip,
        utcOffsetMinutes: getUtcOffsetMinutes(),
        userId: userId ?? undefined,
        adminClientCompanyId: userId != null ? undefined : adminClientCompanyId ?? undefined,
      },
    })
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function markChatRoomNotificationsRead(chatRoomId) {
  return axios
    .post(`${NOTIFICATION_MARK_CHAT_ROOM_READ}/${chatRoomId}`)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function markAllNotificationsRead() {
  return axios.post(NOTIFICATION_MARK_ALL_READ).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getCompanyUsers(companyId) {
  return axios.get(`${REGISTRATIONS_OVERVIEW}/${companyId}/users`).then((response) => {
    if (!response.data?.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result ?? [];
  });
}

export function getRegistrationsOverview({ search, skip = 0, take } = {}) {
  return axios
    .get(REGISTRATIONS_OVERVIEW, { params: { search: search || undefined, skip, take } })
    .then((response) => {
      if (!response.data?.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      const result = response.data.Result ?? {};
      return { rows: result.Items ?? [], totalCount: result.TotalCount ?? 0 };
    });
}

export function getUsers(roleId, adminClientCompanyId) {
  return axios
    .get(
      `${USER}/${roleId}` +
      (adminClientCompanyId ? `/${adminClientCompanyId}/true` : '')
    )
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getAdminClientOwnerClients(adminClientOwnerId) {
  return axios
    .get(ADMIN_CLIENT_OWNER + `/${adminClientOwnerId}/Clients`)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getCalendarUsers(adminClientCompanyId) {
  return axios
    .get(
      adminClientCompanyId
        ? CALENDAR_USERS + `/${adminClientCompanyId}`
        : CALENDAR_USERS
    )
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}
export function getChatRoomUsers() {
  return dedupeRequest('chat-room-users', () =>
    axios.get(CHATROOM_USERS).then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    })
  );
}

export function getPhonebookUsers(adminClientCompanyId) {
  return axios
    .get(
      adminClientCompanyId
        ? PHONEBOOK_USERS + `/${adminClientCompanyId}`
        : PHONEBOOK_USERS
    )
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getUserBase(userId) {
  return axios.get(USER_BASE + '/' + userId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getAdminClientOwnersUsers() {
  return axios.get(ADMIN_CLIENT_OWNER_USERS).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function saveUser(user) {
  return axios.post(USER, user).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function activateUser(data) {
  return axios.put(USER, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function deleteUser(id) {
  return axios.delete(USER + '/' + id).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function checkUserName(id, userName) {
  return axios.get(USER + '/' + id + '/' + userName).then((response) => {
    return response.data.Success;
  });
}

export function checkEmail(id, email) {
  const data = { EmailAddress: email, UserId: id };
  return axios.post(USER_EMAIL, data).then((response) => {
    return { success: response.data.Success, message: response.data.Message };
  });
}

export function getCompany(companyId) {
  return axios.get(COMPANY + '/' + companyId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

export function saveCompany(company) {
  return axios.post(COMPANY, company).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getClients() {
  return axios.get(CLIENT).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getClientsForAdminClient(adminClientId) {
  return axios
    .get(
      CLIENTS_FOR_ADMIN_CLIENT +
      (adminClientId != null ? '/' + adminClientId : '')
    )
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getAssistants() {
  return axios.get(ASSISTANT).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getAssistantsForClient(clientId, adminClientCompanyId = null) {
  return axios
    .get(
      ASSISTANT +
      '/' +
      clientId +
      (adminClientCompanyId ? '/' + adminClientCompanyId : '')
    )
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getClientsForAssistant(assistantId) {
  return axios.get(CLIENT + '/' + assistantId).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getAvailableAssistants(clientId, adminClientCompanyId = null) {
  return axios
    .get(
      AVAILABLE_ASSISTANTS +
      '/' +
      clientId +
      (adminClientCompanyId ? '/' + adminClientCompanyId : '')
    )
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function assignAssistantToClient(clientId, assistantId) {
  return axios
    .post(ASSIGN_ASSISTANT, { AssistantID: assistantId, ClientID: clientId })
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function unassignAssistantFromClient(clientId, assistantId) {
  return axios
    .post(UNASSIGN_ASSISTANT, { AssistantID: assistantId, ClientID: clientId })
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export async function getTaskAvailableUsersForCompany(
  adminClientCompanyId = null,
  clientId
) {
  const response = await dedupeRequest(
    `task-available-users:${adminClientCompanyId ?? ''}:${clientId ?? ''}`,
    () =>
      axios.post(TASK_AVAILABLE_USERS_FOR_AC_COMPANY, {
        AdminClientCompanyId: adminClientCompanyId,
        ClientId: clientId,
      })
  );
  if (!response.data.Success) {
    return Promise.reject(new ApiError(response.data));
  } else return response.data.Result;
}

export async function createTask(task) {
  const payload = { ...task };
  if (payload.Description) {
    payload.Description = btoa(unescape(encodeURIComponent(payload.Description)));
  }
  const response = await axios.post(TASK, payload);
  if (!response.data.Success) {
    return Promise.reject(new ApiError(response.data));
  } else return response.data.Result;
}

export async function saveTask(task) {
  const payload = { ...task };
  if (payload.Description) {
    payload.Description = btoa(unescape(encodeURIComponent(payload.Description)));
  }
  const response = await axios.post(TASK, payload);
  if (!response.data.Success) {
    return Promise.reject(new ApiError(response.data));
  } else return { title: task.Title, description: task.Description };
}

export async function deleteTask(taskId) {
  const response = await axios.delete(TASK + '/' + taskId);
  if (!response.data.Success) {
    return Promise.reject(new ApiError(response.data));
  } else return response.data.Result;
}

export function getTask(taskId) {
  return dedupeRequest(`task:${taskId}`, () =>
    axios.get(TASK + '/' + taskId).then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else return response.data.Result;
    })
  );
}

export function getTasks(userId) {
  return axios.get(TASKS + '/' + userId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

export function getGuestAvailableTasks(guestId) {
  return axios.get(GUEST_AVAILABLE_TASKS + '/' + guestId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

export async function getTaskStatuses() {
  const response = await dedupeRequest('task-statuses', () =>
    axios.get(TASK_STATUSES)
  );
  if (!response.data.Success) {
    return Promise.reject(new ApiError(response.data));
  } else return response.data.Result;
}

export function postTaskStatus(taskStatus) {
  return axios.post(TASK_STATUS, taskStatus).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return taskStatus.StatusID;
  });
}

export async function postTaskAssignees(taskAssignee) {
  const response = await axios.post(TASK_ASSIGNEE, taskAssignee);
  if (!response.data.Success) {
    return Promise.reject(new ApiError(response.data));
  } else return taskAssignee.AssigneeID;
}

export function getTaskComments(taskId) {
  return dedupeRequest(`task-comments:${taskId}`, () =>
    axios.get(TASK_COMMENTS + '/' + taskId).then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else return response.data.Result;
    })
  );
}

export function getLatestComments(taskId, commentId) {
  return axios
    .get(LATEST_COMMENTS + '/' + taskId + '/' + commentId)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else return response.data.Result;
    });
}

export function postTaskComment(taskComment) {
  return axios.post(TASK_COMMENTS, taskComment).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

export function deleteTaskComment(commentId) {
  return axios.delete(TASK_COMMENTS + '/' + commentId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

export function getDocuments(taskId) {
  return dedupeRequest(`task-documents:${taskId}`, () =>
    axios.get(DOCUMENTS + '/' + taskId).then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else return response.data.Result;
    })
  );
}

export function getLatestDocuments(taskId, documentId) {
  return axios
    .get(LATEST_DOCUMENTS + '/' + taskId + '/' + documentId)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else return response.data.Result;
    });
}

export function getDocument(documentId, documentName, download = false) {
  return axios
    .get(`${DOCUMENT}/${documentId}/Access`, { withCredentials: true, params: download ? { download: true } : undefined })
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }

      const result = response.data.Result;
      if (!result?.legacy && result?.url) {
        return result;
      }

      return axios({
        url: DOCUMENT + '/' + documentId,
        method: 'GET',
        responseType: 'blob',
      }).then((documentResponse) => {
        var fileDownload = require('react-file-download');
        fileDownload(new Blob([documentResponse.data]), documentName);
        return { legacy: true };
      });
    });
}

export function postMultipartDocument(file, taskId) {
  return axios
    .post(`${MEDIA_STORAGE}/tasks/presign`, {
      taskId,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    })
    .then(getResponseResult)
    .then(async (upload) => {
      await uploadToPresignedUrl(upload, file);

      const completeResponse = await axios.post(`${MEDIA_STORAGE}/tasks/complete`, {
        taskId,
        key: getUploadValue(upload, 'Key'),
        fileName: file.name,
        fileType: getFileExtension(file.name),
        fileSizeBytes: file.size,
      });

      return getResponseResult(completeResponse);
    });
}

// Description images pasted from articles are often full-resolution (1-5 MB
// each), which made saving take 10+ seconds. Downscale/re-encode before upload:
// display size in a description never needs more than ~1600px.
// Exported so editors can run this at INSERT time (in the background, while
// the user is still editing) instead of at save - Jira-style: by the time the
// user hits save, the heavy encode work is already done and only the (small)
// upload remains. Callers of uploadTaskImage are expected to have done this.
const IMAGE_MAX_DIMENSION = 1600;
const IMAGE_COMPRESS_THRESHOLD_BYTES = 300 * 1024;

export async function compressImageForUpload(file) {
  const type = (file.type || '').toLowerCase();
  // gif is skipped so animations survive; unknown types pass through untouched.
  const compressible = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!compressible.includes(type)) return file;
  if (file.size <= IMAGE_COMPRESS_THRESHOLD_BYTES) return file;

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, IMAGE_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    // jpeg stays jpeg; png/webp re-encode as webp (keeps transparency). Safari
    // can't encode webp and falls back to png, which the size check below handles.
    const targetType = type === 'image/png' || type === 'image/webp' ? 'image/webp' : 'image/jpeg';
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, targetType, 0.82));
    if (!blob || blob.size >= file.size) return file;

    const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];
    const baseName = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.${ext}`, { type: blob.type });
  } catch (error) {
    return file;
  }
}

// Direct (non-staged) upload of an image as a task attachment: presign +
// complete in one go. NOTE: the description editors no longer use this - they
// stage at insert (uploadTaskImageStaged) and commit at save (commitTaskImage)
// so abandoned edits leave no attachment. Kept for potential non-editor
// callers. Compression is the caller's job (compressImageForUpload).
export async function uploadTaskImage(file, taskId) {
  const uploadFile = file;

  const presignResponse = await axios.post(`${MEDIA_STORAGE}/tasks/presign`, {
    taskId,
    fileName: uploadFile.name,
    contentType: uploadFile.type || 'application/octet-stream',
  });

  const upload = await getResponseResult(presignResponse);
  await uploadToPresignedUrl(upload, uploadFile);

  const key = getUploadValue(upload, 'Key');

  const completeResponse = await axios.post(`${MEDIA_STORAGE}/tasks/complete`, {
    taskId,
    key,
    fileName: uploadFile.name,
    fileType: getFileExtension(uploadFile.name),
    fileSizeBytes: uploadFile.size,
  });

  const completeResult = await getResponseResult(completeResponse);
  const documentId =
    completeResult?.documentId ?? completeResult?.DocumentID ?? completeResult;

  return { documentId, key };
}

// Description images upload to a task-agnostic S3 staging prefix the moment
// they are inserted in the editor - no DB record is created; an S3 lifecycle
// rule expires whatever never gets committed (abandoned edits). Compression
// is the caller's job (compressImageForUpload). Returns what commitTaskImage
// needs at save time.
export async function uploadTaskImageStaged(file, signal) {
  const presignResponse = await axios.post(
    `${MEDIA_STORAGE}/tasks/presign-staging`,
    {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    },
    { signal }
  );

  const upload = await getResponseResult(presignResponse);
  await uploadToPresignedUrl(upload, file, signal);

  return {
    stagingKey: getUploadValue(upload, 'Key'),
    fileName: file.name,
    fileSizeBytes: file.size,
  };
}

// Description save: promotes a staged image under the task's prefix and
// registers it as a task document. Returns { documentId, key } where key is
// the final storage key the persisted html should reference.
export async function commitTaskImage({ stagingKey, fileName, fileSizeBytes }, taskId) {
  const response = await axios.post(`${MEDIA_STORAGE}/tasks/commit`, {
    taskId,
    stagingKey,
    fileName,
    fileType: getFileExtension(fileName),
    fileSizeBytes,
  });

  const result = await getResponseResult(response);
  return {
    documentId: getUploadValue(result, 'DocumentId'),
    key: getUploadValue(result, 'Key'),
  };
}

// Resolves the stable storage key stored in a description <img src> to a fresh
// protected (signed) url. Returns null when access is denied so callers can
// decide how to render the broken image.
export function resolveTaskImageUrl(key, taskId) {
  return grantMediaAccess(key, { taskId });
}

export function deleteDocument(documentId) {
  return axios.delete(DOCUMENT + '/' + documentId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

// Unload-safe document delete. axios requests are cancelled when the page is
// closing/refreshing, so for pagehide/beforeunload cleanup we use fetch with
// keepalive (which can still carry the Authorization header, unlike sendBeacon).
export function deleteDocumentBeacon(documentId) {
  const authToken = localStorage.getItem('authToken');
  try {
    return fetch(`${DOCUMENT}/${documentId}`, {
      method: 'DELETE',
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      keepalive: true,
    }).catch(() => { });
  } catch (error) {
    return Promise.resolve();
  }
}

export function getGlobalSettings() {
  return axios.get(SETTINGS).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

export function saveGlobalSetting(data) {
  return axios.post(SETTINGS, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data;
  });
}

async function saveAvatarViaMediaStorage(image, userId = null) {
  const originalFile = dataUrlToFile(image, 'avatar.png');
  const { file: compressedFile, error } = await prepareAvatar(originalFile);
  if (error) {
    throw new Error(error);
  }

  const file = compressedFile;
  const presignResponse = await axios.post(`${MEDIA_STORAGE}/avatars/presign`, {
    userId,
    fileName: file.name,
    contentType: file.type,
    includeThumbnail: false,
  });
  const upload = await getResponseResult(presignResponse);
  const avatarUpload = upload.avatar ?? upload.Avatar;

  await uploadToPresignedUrl(avatarUpload, file);

  const completeResponse = await axios.post(`${MEDIA_STORAGE}/avatars/complete`, {
    userId,
    key: getUploadValue(avatarUpload, 'Key'),
  });

  const completed = await getResponseResult(completeResponse);
  return completed?.avatar ?? completed?.Avatar ?? image;
}

export function saveAvatar(image) {
  return saveAvatarViaMediaStorage(image);
}

export function saveAvatarForUser(image, userId) {
  return saveAvatarViaMediaStorage(image, userId);
}

export function getChatMessages(userId, loggedUser) {
  return dedupeRequest(`chat-messages:${userId}`, () =>
    axios.get(CHAT_MESSAGES + '/' + userId).then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else {
        return {
          chatGroups: response.data.Result,
          loggedUser,
        };
      }
    })
  );
}
export function getChatRoomMessages(chatRoomId, lastId, messagesCount) {
  return axios
    .get(`${CHAT_ROOM_MESSAGES}/${chatRoomId}/${lastId}/${messagesCount}`)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else return response.data.Result;
    });
}

export function getOverseenChatGroups(userId) {
  return axios.get(`${CHAT_GROUPS_OVERSEEN}${userId}`).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getOverseenChatRoomMessages(
  roomId,
  messagesCount = INTIAL_OVERSEEN_MESSAGE_COUNT_FETCH
) {
  return axios
    .post(CHAT_ROOM_MESSAGES_OVERSEEN, {
      ChatRoomID: roomId,
      MessagesCount: messagesCount,
    })
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getOverseenAdditinalChatRoomMessages(
  roomId,
  lastChatMessageId
) {
  return axios
    .get(
      `${CHAT_ROOM_MESSAGES_OVERSEEN_ADDITIONAL}/${roomId}/${lastChatMessageId}`
    )
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getGuestsForTask(taskId) {
  return dedupeRequest(`task-guests:${taskId}`, () =>
    axios.get(GUEST_TASK + '/' + taskId).then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else return response.data.Result;
    })
  );
}

export function getExistingGuests(taskId) {
  return axios.get(GUEST_EXISTING + '\\' + taskId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

export function getGuestsForUser(userId) {
  return axios.get(GUEST_USER + '/' + userId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

export function addNewGuestToTask(
  firstName,
  lastName,
  email,
  taskId,
  guestUserId,
  adminClientCompanyId = null
) {
  return axios
    .post(GUEST_TASK, {
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      TaskID: taskId,
      GuestID: guestUserId === undefined ? null : guestUserId,
      adminClientCompanyId: adminClientCompanyId,
    })
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else return response.data.Result;
    });
}

export function deleteGuestFromTask(guestId, taskId) {
  return axios
    .post(DELETE_GUEST_TASK, { GuestID: guestId, TaskID: taskId })
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      } else return response.data.Result;
    });
}

export async function sendInviteEmail(data) {
  try {
    const res = await axios.post(CREATE_USER_WITH_REGISTRATION_LINK, data);
    return res;
  } catch (error) {
    return error.response;
  }
}
export async function resendRegistrationLink(data) {
  try {
    const res = await axios.post(RESEND_REGISTRATION_LINK, data);
    return res;
  } catch (error) {
    return error.response;
  }
}
export async function getEmailInvitations(companyId) {
  try {
    const response = await axios.get(GET_EMAIL_INVITATIONS, {
      params: {
        AdminClientCompanyId: companyId,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
}

export async function registerUserWithLink(data) {
  try {
    const res = await axios.post(REGISTER_USER_WITH_LINK, data);
    return res.data;
  } catch (error) {
    const serverMessage = error.response?.data?.Message || error.response?.data;
    const message =
      typeof serverMessage === 'string' && serverMessage
        ? serverMessage
        : 'An error occurred. Please try again.';
    throw new Error(message);
  }
}

export function createUser(data) {
  return axios
    .post(CREATE_USER, data)
    .then((res) => {
      if (res.data && res.data.Success === false) {
        throw new Error(res.data.Message || 'An error occurred. Please try again.');
      }

      const result = (res.data && res.data.Result) || {};

      return {
        ...result,
        message:
          (res.data && res.data.Message) ||
          "User '" + data.User_Username + "' has been successfully created!",
      };
    })
    .catch((error) => {
      const serverMessage = error.response?.data?.Message || error.response?.data;
      const message =
        typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : error.message || 'An error occurred. Please try again.';
      throw new Error(message);
    });
}

export function getSpecificUser(userId) {
  return axios.get(SPECIFIC_USER + '/' + userId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return response.data.Result;
  });
}

export function getUserSettings(userId) {
  return axios.get(USER_SETTINGS + '/' + userId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function insertUserSettings(data) {
  return axios.post(USER_SETTINGS, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return data;
  });
}

export function updateUserSettings(data) {
  return axios.put(USER_SETTINGS, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    } else return data;
  });
}

export function getNotificationFrequencies() {
  return axios.get(NOTIFICATION_FREQUENCIES).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function logUserActivity(userActivityTypeId) {
  const now = Date.now();
  const lastRequestAt = lastUserActivityRequest.get(userActivityTypeId) || 0;
  if (now - lastRequestAt < USER_ACTIVITY_THROTTLE_MS) {
    return Promise.resolve();
  }

  lastUserActivityRequest.set(userActivityTypeId, now);

  //return Promise.resolve();
  return axios
    .put(USER_ACTIVITY + '/' + userActivityTypeId)
    .then((response) => {
      if (!response.data.Success) {
        //return Promise.reject(new LogActivityError(response.data));
        //return Promise.reject(new ApiError(response.data));
        //TODO:Error handling after logging user activity, when token is expired, or user is deactivated
        return Promise.resolve(); //Because token expiration case, otherwise it will crash app
      } else return response.data.Result;
    })
    .catch(() => { });
}

export function getCalendarEntryTypes() {
  return axios.get(CALENDAR_ENTRY_TYPES).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getCalendarEntryReminderTime() {
  return axios.get(CALENDAR_ENTRY_REMINDER_TIME).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getCalendarEntryRecurrences() {
  return axios.get(CALENDAR_ENTRY_RECURRENCES).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getCalendarEntries(userId, date, adminClientCompanyId) {
  const data = {
    DateTimeComplete: moment(date).format("YYYY-MM-DDTHH:mm:ssZ"),
    UserID: userId,
    AdminClientCompanyID: adminClientCompanyId,
  };

  return axios.post(CALENDAR_ENTRIES, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function saveCalendarEntry(data) {
  return axios.post(CALENDAR_ENTRY, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function deleteCalendarEntry(id) {
  return axios.delete(CALENDAR_ENTRY + '/' + id).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getCalendarMonthlyAvailability(
  userId,
  year,
  month,
  dateTimeComplete,
  adminClientCompanyId
) {
  const data = {
    UserID: userId,
    Year: year,
    Month: month,
    DateTimeComplete: dateTimeComplete,
    AdminClientCompanyID: adminClientCompanyId,
  };

  return axios.post(CALENDAR_MONTHLY_AVAILABILITY, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getAdminClientCompanyWithOwner(adminClientCompanyid) {
  return axios
    .get(ADMIN_CLIENT_COMPANY + `/${adminClientCompanyid}/AdminClientOwner`)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getContacts(userId) {
  return axios.get(PHONEBOOK_CONTACTS + '/' + userId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getAdminClientCompanyContacts(adminClientCompanyId) {
  return axios
    .get(PHONEBOOK_AC_COMPANY_CONTACTS + '/' + adminClientCompanyId)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function deleteContacts(data, userId, adminClientCompanyId) {
  return axios.post(PHONEBOOK_CONTACTS_DELETE, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return { userId, adminClientCompanyId };
  });
}

export function getContactDetailTypes() {
  return axios.get(PHONEBOOK_CONTACT_DETAIL_TYPES).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function saveContact(value) {
  return axios.post(PHONEBOOK_CONTACT, value).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return {
      userId: value.UserID,
      adminClientCompanyId: value.AdminClientCompanyId,
    };
  });
}

export function deleteContact(id, userId, adminClientCompanyId) {
  return axios.delete(PHONEBOOK_CONTACT + '/' + id).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return { userId, adminClientCompanyId };
  });
}

export function importContacts(data, userId) {
  return axios
    .post(PHONEBOOK_CONTACTS + '/' + userId, data)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getPushOnSettings(userId) {
  return axios.get(PUSH_ON_SETTINGS + '/' + userId).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

// Date.getTimezoneOffset() returns minutes to ADD to local time to get UTC
// (inverted from the conventional "UTC offset" - e.g. +420 for UTC-7).
// Negate it so the backend receives the conventional local-minus-UTC minutes
// (-420 for UTC-7, +330 for UTC+5:30), matching TimeSpan.FromMinutes usage
// server-side (NotificationController).
function getUtcOffsetMinutes() {
  return -new Date().getTimezoneOffset();
}

export function getNotificationData() {
  return dedupeRequest('notification-data', () =>
    axios
      .get(NOTIFICATION_DATA, { params: { utcOffsetMinutes: getUtcOffsetMinutes() } })
      .then((response) => {
        if (!response.data.Success) {
          return Promise.reject(new ApiError(response.data));
        }
        return response.data.Result;
      })
  );
}

export function savePushOnSettingsForClient(data) {
  return axios.post(PUSH_ON_CLIENT_DATA, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }

    return response.data.Result;
  });
}

export function getAdminNotificationData() {
  return axios.get(ADMIN_NOTIFICATION_DATA).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getTaskDocumentNotificationData(taskId) {
  return axios
    .get(`${TASK_DOCUMENT_NOTIFICATION_DATA}/${taskId}`)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getTaskCommentNotificationData(taskId) {
  return axios
    .get(`${TASK_COMMENT_NOTIFICATION_DATA}/${taskId}`)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function getTaskNotificationData(taskId) {
  return axios.get(`${TASK_NOTIFICATION_DATA}/${taskId}`).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getCalendarNotificationData() {
  // Deduped: marking an entry seen and the SignalR echo both ask for this at
  // the same moment, which fired the same query twice on every read.
  return dedupeRequest('calendar-notification-data', () =>
    axios
      .get(CALENDAR_NOTIFICATION_DATA, { params: { utcOffsetMinutes: getUtcOffsetMinutes() } })
      .then((response) => {
        if (!response.data.Success) {
          return Promise.reject(new ApiError(response.data));
        }
        return response.data.Result;
      })
  );
}

export function setTaskCommentsSeen(data) {
  return axios.put(SET_TASK_COMMENTS_SEEN, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function setCalendarEntrySeen(data) {
  return axios.put(CALENDAR_ENTRY_SEEN, data).then((response) => {
    if (!response.data.Success) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.Result;
  });
}

export function getCreateUsersLimit() {
  return axios.get(CREATE_USER_LIMIT).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function uploadFile(selectedFile, roomId, type, { onProgress } = {}) {
  const file = extractFile(selectedFile);
  const reportMediaProgress = onProgress ? (value) => onProgress(Math.min(value, 1) * 0.95) : undefined;

  return createChatThumbnail(file, type).then((thumbnailFile) => axios
    .post(`${MEDIA_STORAGE}/chat/presign`, {
      roomId,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      mediaType: type,
      includeThumbnail: Boolean(thumbnailFile),
    })
    .then(getResponseResult)
    .then(async (upload) => {
      const mediaUpload = upload.media ?? upload.Media;
      const thumbnailUpload = upload.thumbnail ?? upload.Thumbnail;

      await uploadToPresignedUrl(mediaUpload, file, uploadTimeoutSignal(file.size), reportMediaProgress);

      let thumbnailKey = null;
      if (thumbnailFile && thumbnailUpload) {
        await uploadToPresignedUrl(thumbnailUpload, thumbnailFile, uploadTimeoutSignal(thumbnailFile.size));
        thumbnailKey = getUploadValue(thumbnailUpload, 'Key');
      }

      const completeResponse = await axios.post(`${MEDIA_STORAGE}/chat/complete`, {
        roomId,
        key: getUploadValue(mediaUpload, 'Key'),
        thumbnailKey,
        mediaType: type,
      });

      const result = getResponseResult(completeResponse);
      onProgress?.(1);
      return result;
    }));
}

export function getAdminUsersLimit(userId) {
  return axios.get(`${CREATE_USER_LIMIT}/${userId}`).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function getChatNotificationData() {
  return axios
    .get(`${API_BASE_URL}/Notification/Data/Chat`)
    .then((response) => {
      if (!response.data.Success) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data.Result;
    });
}

export function removeService(subscriptionItemId) {
  return axios
    .post(`${API_BASE_URL}/subscriptions/cancelsubscriptionitem`, {
      SubscriptionItemId: subscriptionItemId,
    })
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data;
    });
}

export function addNewSubscriptionItem(priceId, coupons) {
  return axios
    .post(`${API_BASE_URL}/subscriptions/addsubscriptionitem`, {
      PriceId: priceId,
      Coupons: coupons,
    })
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data;
    });
}

// export function upgradeUserPackage(priceId) {
//   return axios
//     .post(`${API_BASE_URL}/subscriptions/upgradesubscription`, {
//       NewPriceId: priceId,
//     })
//     .then((response) => {
//       if (response.status !== 200) {
//         return Promise.reject(new ApiError(response.data));
//       }
//       return response.data;
//     });
// }
// export function upgradeUserPackage(priceId, coupons, quantity) {

//   return axios
//     .post(`${API_BASE_URL}/subscriptions/syncSubscription`, {
//       NewPriceId: priceId,
//       Coupons: coupons,
//       Quantity: quantity,
//     })
//     .then((response) => {
//       // Axios only executes this block for 2xx successful responses
//       return response.data;
//     })
//     .catch((error) => {
//       // Axios catches 4xx and 5xx errors here
//       if (error.response && error.response.data) {
//         // The backend message might be a direct string or wrapped in a Message object
//         const backendMessage = error.response.data.Message || error.response.data;

//         console.error("Backend Error:", backendMessage);

//         // Pass the actual backend message to your ApiError handler
//         return Promise.reject(new ApiError(backendMessage));
//       }

//       // Fallback just in case it's a network error (e.g., server is down)
//       return Promise.reject(new ApiError(error.message));
//     });
// }
export function upgradeUserPackage(priceId, coupons) {

  return axios
    .post(`${API_BASE_URL}/subscriptions/upgradesubscription`, {
      NewPriceId: priceId,
      Coupons: coupons,
    })
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data;
    });
}
export function addCertificate(priceId, coupons, quantity) {

  return axios
    .post(`${API_BASE_URL}/subscriptions/addCertificate`, {
      PriceId: priceId,
      Coupons: coupons,
      Quantity: quantity,
    })
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data;
    });
}
export function viewInvoice(invoiceId) {
  const authToken = localStorage.getItem('authToken');
  return axios
    .get(`${API_BASE_URL}/invoices/${invoiceId}/pdf?download=false`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${authToken}` },
    })
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      return response;
    });
}

export function downloadInvoice(invoiceId, invoiceNumber) {
  const authToken = localStorage.getItem('authToken');
  return axios
    .get(`${API_BASE_URL}/invoices/${invoiceId}/pdf?download=true`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${authToken}` },
    })
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

export function downgradeUserPackage(priceId, coupons) {
  return axios
    .post(`${API_BASE_URL}/subscriptions/downgradesubscription`, {
      NewPriceId: priceId,
      Coupons: coupons,
    })
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data;
    });
}

export function cancelAccount() {
  return axios
    .post(`${API_BASE_URL}/subscriptions/cancelsubscription`)
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data;
    });
}

export function reactivateAccount() {
  return axios
    .post(`${API_BASE_URL}/subscriptions/reactivatesubscription`)
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data;
    });
}

export function markInvoiceAsPaid(invoiceId) {
  return axios
    .post(`${API_BASE_URL}/invoices/markinvoiceaspaid`, {
      InvoiceId: invoiceId,
    })
    .then((response) => {
      if (response.status !== 200) {
        return Promise.reject(new ApiError(response.data));
      }
      return response.data;
    });
}

export function getStripeCustomer() {
  return axios.get(GET_STRIPE_CUSTOMER).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function getPaymentMethods() {
  return axios.get(GET_PAYMENT_METHODS).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data.data;
  });
}

export function attachPaymentMethod(id) {
  return axios.post(ATTACH_PAYMENT_METHOD(id)).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function deletePaymentMethod(id) {
  return axios.delete(DELETE_PAYMENT_METHOD(id)).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function changeDefaultPaymentMethod(id) {
  return axios.post(CHANGE_DEFAULT_PAYMENT_METHOD(id)).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function addCoupon(id) {
  return axios.post(ADD_COUPON(), { coupon: id }).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

export function removeCoupon(id) {
  return axios.post(REMOVE_COUPON(), { coupon: id }).then((response) => {
    if (response.status !== 200) {
      return Promise.reject(new ApiError(response.data));
    }
    return response.data;
  });
}

// export function addCoupon(id) {
//   return axios.post(ADD_COUPON(id)).then((response) => {
//     if (response.status !== 200) {
//       return Promise.reject(new ApiError(response.data));
//     }
//     return response.data;
//   });
// }
// export function removeCoupon(id) {
//   return axios.post(REMOVE_COUPON(id)).then((response) => {
//     if (response.status !== 200) {
//       return Promise.reject(new ApiError(response.data));
//     }
//     return response.data;
//   });
// }

// Billing Sync admin page. These endpoints are Admin-only and return plain JSON,
// not the { Success, Result } envelope the older endpoints use.
export function getHeldDeactivations() {
  return axios.get(STRIPE_SYNC_HELD_DEACTIVATIONS).then((response) => response.data ?? []);
}

export function approveHeldDeactivation(companyId) {
  return axios.post(`${STRIPE_SYNC_HELD_DEACTIVATIONS}/${companyId}/approve`).then((response) => response.data);
}

export function dismissHeldDeactivation(companyId) {
  return axios.post(`${STRIPE_SYNC_HELD_DEACTIVATIONS}/${companyId}/dismiss`).then((response) => response.data);
}

export function getReconciliationReport() {
  return axios.get(STRIPE_SYNC_RECONCILIATION).then((response) => response.data);
}

export function runReconciliation() {
  return axios.post(`${STRIPE_SYNC_RECONCILIATION}/run`).then((response) => response.data);
}

const syncIssue = (direction, issue) =>
  axios
    .post(`${STRIPE_SYNC_RECONCILIATION}/${direction}`, {
      IssueType: issue.IssueType,
      StripeCustomerId: issue.StripeCustomerId,
      SubscriptionId: issue.SubscriptionId,
    })
    .then((response) => response.data);

export function syncIssueFromStripe(issue) {
  return syncIssue('sync-from-stripe', issue);
}

export function syncIssueToStripe(issue) {
  return syncIssue('sync-to-stripe', issue);
}

export function getFailedStripeEvents() {
  return axios.get(STRIPE_SYNC_FAILED_EVENTS).then((response) => response.data ?? []);
}

export function retryFailedStripeEvent(eventId) {
  return axios.post(`${STRIPE_SYNC_FAILED_EVENTS}/${encodeURIComponent(eventId)}/retry`).then((response) => response.data);
}

export async function setDefaultPaymentMethodFromIntent(intentId) {
  const authToken = localStorage.getItem('authToken');
  if (!authToken) return null;

  setAuthorizationToken(authToken);
  const response = await axios.post(DEFAULT_PAYMENT_METHOD_FROM_INTENT(encodeURIComponent(intentId)), null, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return response.data;
}

export async function getSubscriptionStatus(since) {
  try {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      setAuthorizationToken(null);
      return { status: 'missing-auth' };
    }

    setAuthorizationToken(authToken);
    const url = since
      ? `${SUBSCRIPTION_STATUS}?since=${encodeURIComponent(since)}`
      : SUBSCRIPTION_STATUS;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (response.data?.StatusCode === 401) {
      return { status: 'unauthorized' };
    }
    if (response.status !== 200) return null;
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      const retryAfter = parseInt(error.response.headers?.['retry-after'], 10);
      return {
        status: 'rate-limited',
        retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : 60,
      };
    }
    return null;
  }
}
